// src/components/MemberSettings.jsx
import React, { useState, useEffect, useRef } from 'react';
import { getAuth, updatePassword, reauthenticateWithCredential, EmailAuthProvider, deleteUser, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { getFirestore, doc, getDoc, updateDoc } from "firebase/firestore";
import { 
  User, 
  Camera, 
  Lock, 
  Mail, 
  Shield, 
  Bell, 
  Monitor, 
  Moon, 
  Sun,
  Save,
  X,
  AlertTriangle,
  CheckCircle,
  Eye,
  EyeOff,
  Trash2
} from "lucide-react";
import '../Styles/settings.css';

// Delete Confirmation Modal Component
const DeleteAccountModal = ({ isOpen, onClose, onConfirm, loading }) => {
  const [deletePassword, setDeletePassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [useGoogle, setUseGoogle] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const auth = getAuth();
  const user = auth.currentUser;

  const handleGoogleVerification = async () => {
    try {
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
      onConfirm(null, true); // Pass null for password, true for Google auth
    } catch (error) {
      console.error('Google verification failed:', error);
    }
  };

  const handlePasswordConfirm = () => {
    if (confirmText !== 'DELETE MY ACCOUNT') {
      alert('Please type "DELETE MY ACCOUNT" to confirm');
      return;
    }
    onConfirm(deletePassword, false);
  };

  const isGoogleUser = user?.providerData?.some(provider => provider.providerId === 'google.com');

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content delete-modal">
        <div className="modal-header">
          <div className="modal-title-icon">
            <AlertTriangle size={28} className="warning-icon" />
          </div>
          <h2>Delete Account</h2>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        <div className="modal-body">
          <div className="warning-box">
            <p className="warning-title">⚠️ This action is permanent</p>
            <p className="warning-text">
              Deleting your account will permanently remove all your data, courses progress, and subscription information. This cannot be undone.
            </p>
          </div>

          <div className="verification-section">
            <h3>Verify your identity to continue</h3>

            {/* Password Verification */}
            {!useGoogle && (
              <div className="verification-method">
                <h4>Password Verification</h4>
                <div className="form-group">
                  <label htmlFor="deletePassword" className="form-label">Enter Your Password</label>
                  <div className="password-input">
                    <input
                      type={showPassword ? "text" : "password"}
                      id="deletePassword"
                      className="form-input"
                      value={deletePassword}
                      onChange={(e) => setDeletePassword(e.target.value)}
                      placeholder="Enter your password"
                      disabled={loading}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      disabled={loading}
                    >
                      {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmDelete" className="form-label">Type to Confirm</label>
                  <input
                    type="text"
                    id="confirmDelete"
                    className="form-input"
                    value={confirmText}
                    onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
                    placeholder='Type "DELETE MY ACCOUNT"'
                    disabled={loading}
                  />
                  <p className="input-hint">Type DELETE MY ACCOUNT to confirm deletion</p>
                </div>

                {isGoogleUser && (
                  <button
                    type="button"
                    className="switch-verification-btn"
                    onClick={() => setUseGoogle(true)}
                    disabled={loading}
                  >
                    Verify with Google instead →
                  </button>
                )}
              </div>
            )}

            {/* Google Verification */}
            {useGoogle && isGoogleUser && (
              <div className="verification-method">
                <h4>Google Verification</h4>
                <p className="verification-hint">
                  Click below to verify your identity with your Google account.
                </p>
                <button
                  type="button"
                  className="google-verify-btn"
                  onClick={handleGoogleVerification}
                  disabled={loading}
                >
                  <svg className="google-icon-small" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Verify with Google
                </button>
                <button
                  type="button"
                  className="switch-verification-btn"
                  onClick={() => setUseGoogle(false)}
                  disabled={loading}
                >
                  ← Use password instead
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="modal-cancel-btn"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            className="modal-delete-btn"
            onClick={useGoogle ? handleGoogleVerification : handlePasswordConfirm}
            disabled={loading || (confirmText !== 'DELETE MY ACCOUNT' && !useGoogle)}
          >
            {loading ? 'Deleting...' : 'Delete Account Permanently'}
          </button>
        </div>
      </div>
    </div>
  );
};

const MemberSettings = () => {
  const [userData, setUserData] = useState({
    displayName: '',
    email: '',
    role: '',
    subscriptionStatus: '',
    subscriptionEnd: null,
    profilePicture: ''
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [editMode, setEditMode] = useState(false);
  const [updatedInfo, setUpdatedInfo] = useState({
    displayName: '',
    email: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [profilePicturePreview, setProfilePicturePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  
  const fileInputRef = useRef(null);

  const auth = getAuth();
  const db = getFirestore();
  const user = auth.currentUser;

  // Fetch user data
  useEffect(() => {
    const fetchUserData = async () => {
      if (!user) return;
      
      setLoading(true);
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserData({
            displayName: data.displayName || '',
            email: user.email,
            role: data.role || 'member',
            subscriptionStatus: data.subscriptionStatus || 'inactive',
            subscriptionEnd: data.subscriptionEnd ? data.subscriptionEnd.toDate().toLocaleDateString() : null,
            profilePic: data.profilePic || data.profilePicture
          });
          setUpdatedInfo({
            displayName: data.displayName || '',
            email: user.email
          });
          
          if (data.profilePic) {
            setProfilePicturePreview(data.profilePic);
          } else if (data.profilePicture) {
            setProfilePicturePreview(data.profilePicture);
          }
        }
      } catch (error) {
        setMessage({ text: `Error fetching user data: ${error.message}`, type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, [user, db]);

  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match('image.*')) {
        setMessage({ text: 'Please select an image file', type: 'error' });
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        setMessage({ text: 'Image size should be less than 5MB', type: 'error' });
        return;
      }
      
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64String = reader.result;
        setProfilePicturePreview(base64String);
        setUpdatedInfo(prev => ({ ...prev, profilePicture: base64String }));
      };
      reader.readAsDataURL(file);
    }
  };

  const triggerFileInput = () => {
    fileInputRef.current.click();
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setMessage({ text: 'Passwords do not match', type: 'error' });
      return;
    }
    
    if (newPassword.length < 6) {
      setMessage({ text: 'Password should be at least 6 characters', type: 'error' });
      return;
    }
    
    setLoading(true);
    try {
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
      await reauthenticateWithCredential(user, credential);
      await updatePassword(user, newPassword);
      setMessage({ text: 'Password updated successfully', type: 'success' });
      setNewPassword('');
      setConfirmPassword('');
      setCurrentPassword('');
    } catch (error) {
      setMessage({ text: `Error updating password: ${error.message}`, type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setIsUploading(true);
    try {
      const updateData = {
        displayName: updatedInfo.displayName
      };
      
      if (updatedInfo.profilePicture && updatedInfo.profilePicture !== userData.profilePicture) {
        updateData.profilePicture = updatedInfo.profilePicture;
      }
      
      await updateDoc(doc(db, 'users', user.uid), updateData);
      
      setUserData({
        ...userData,
        displayName: updatedInfo.displayName,
        profilePicture: updatedInfo.profilePicture
      });
      
      setEditMode(false);
      setMessage({ text: 'Profile updated successfully', type: 'success' });
    } catch (error) {
      setMessage({ text: `Error updating profile: ${error.message}`, type: 'error' });
    } finally {
      setLoading(false);
      setIsUploading(false);
    }
  };

  const handleDeleteAccount = async (password, useGoogle) => {
    setLoading(true);
    try {
      const userId = user.uid;
      const userEmail = user.email;
      
      // Step 1: Re-authenticate the user
      if (useGoogle) {
        // User already re-authenticated via Google popup in the modal
        console.log('Using Google authentication for deletion');
      } else {
        // Use password for re-authentication
        const credential = EmailAuthProvider.credential(userEmail, password);
        await reauthenticateWithCredential(user, credential);
      }
      
      // Step 2: Delete from Firestore first
      try {
        const userDocRef = doc(db, 'users', userId);
        await updateDoc(userDocRef, {
          deleted: true,
          deletedAt: new Date().toISOString(),
          email: '', // Clear sensitive data
          name: 'Deleted User'
        });
        console.log('Firestore document updated');
      } catch (firestoreError) {
        console.error('Error updating Firestore:', firestoreError);
        // Continue with auth deletion even if Firestore update fails
      }
      
      // Step 3: Delete Firebase Authentication user
      // This will also automatically delete all associated data
      await deleteUser(user);
      console.log('User deleted from Authentication');
      
      setMessage({ text: 'Account and all associated data deleted successfully', type: 'success' });
      setShowDeleteModal(false);
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        window.location.href = '/login';
      }, 2000);
    } catch (error) {
      console.error('Delete account error:', error);
      
      // Provide specific error messages
      let errorMessage = 'Error deleting account: ';
      if (error.code === 'auth/requires-recent-login') {
        errorMessage += 'Please sign out and sign in again before deleting your account.';
      } else if (error.code === 'auth/invalid-login-credentials') {
        errorMessage += 'Invalid password. Please try again.';
      } else if (error.code === 'auth/user-token-expired') {
        errorMessage += 'Your session expired. Please sign out and sign in again.';
      } else {
        errorMessage += error.message;
      }
      
      setMessage({ text: errorMessage, type: 'error' });
      setLoading(false);
    }
  };

  useEffect(() => {
    if (message.text) {
      const timer = setTimeout(() => {
        setMessage({ text: '', type: '' });
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  return (
    <div className="settings-container">
      <div className="settings-header">
        <h1>Settings</h1>
        <p>Manage your account settings and preferences</p>
      </div>
      
      {message.text && (
        <div className={`message ${message.type}`}>
          {message.type === 'success' ? (
            <CheckCircle size={20} />
          ) : (
            <AlertTriangle size={20} />
          )}
          {message.text}
        </div>
      )}
      
      <div className="settings-grid">
        {/* Profile Card */}
        <div className="settings-card profile-card">
          <div className="card-header">
            <h2>Profile Information</h2>
            {editMode ? (
              <div className="edit-actions">
                <button 
                  className="cancel-btn" 
                  onClick={() => {
                    setEditMode(false);
                    setUpdatedInfo({
                      displayName: userData.displayName,
                      email: userData.email,
                      profilePicture: userData.profilePicture
                    });
                    setProfilePicturePreview(userData.profilePicture);
                  }}
                >
                  <X size={18} />
                </button>
                <button 
                  className="save-btn" 
                  onClick={handleProfileUpdate}
                  disabled={loading || isUploading}
                >
                  {loading || isUploading ? 'Saving...' : <><Save size={18} /> Save</>}
                </button>
              </div>
            ) : (
              <button 
                className="edit-btn" 
                onClick={() => setEditMode(true)}
              >
                Edit Profile
              </button>
            )}
          </div>
          
          <div className="profile-section">
            <div className="profile-picture-container">
              <div className="profile-picture">
                {profilePicturePreview ? (
                  <img src={profilePicturePreview} alt="Profile" />
                ) : (
                  <div className="profile-picture-placeholder">
                    <User size={48} />
                  </div>
                )}
              </div>
              
              {editMode && (
                <div className="profile-picture-upload">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleProfilePictureChange}
                    accept="image/*"
                    style={{ display: 'none' }}
                  />
                  <button 
                    className="upload-btn"
                    onClick={triggerFileInput}
                  >
                    <Camera size={18} />
                    Change Photo
                  </button>
                </div>
              )}
            </div>
            
            <div className="profile-info">
              <div className="info-item">
                <label className="info-label">Name</label>
                {editMode ? (
                  <input
                    type="text"
                    className="form-input"
                    value={updatedInfo.displayName}
                    onChange={(e) => setUpdatedInfo({...updatedInfo, displayName: e.target.value})}
                    placeholder="Your name"
                  />
                ) : (
                  <span className="info-value">{userData.displayName || 'Not set'}</span>
                )}
              </div>
              
              <div className="info-item">
                <label className="info-label">Email</label>
                {editMode ? (
                  <input
                    type="email"
                    className="form-input"
                    value={updatedInfo.email}
                    onChange={(e) => setUpdatedInfo({...updatedInfo, email: e.target.value})}
                    placeholder="Your email"
                    disabled
                  />
                ) : (
                  <span className="info-value">{userData.email}</span>
                )}
              </div>
              
              <div className="info-item">
                <label className="info-label">Role</label>
                <span className="info-value role-badge">{userData.role || 'member'}</span>
              </div>
              
              <div className="info-item">
                <label className="info-label">Subscription</label>
                <span className={`info-value subscription-badge ${userData.subscriptionStatus === 'active' ? 'active' : 'inactive'}`}>
                  {userData.subscriptionStatus || 'inactive'}
                </span>
              </div>
              
              {userData.subscriptionEnd && (
                <div className="info-item">
                  <label className="info-label">Subscription Ends</label>
                  <span className="info-value">{userData.subscriptionEnd}</span>
                </div>
              )}
            </div>
          </div>
        </div>
        
        {/* Security Card */}
        <div className="settings-card security-card">
          <div className="card-header">
            <h2>Security</h2>
            <Shield size={20} />
          </div>
          
          <form onSubmit={handlePasswordUpdate}>
            <div className="form-group">
              <label htmlFor="currentPassword" className="form-label">Current Password</label>
              <div className="password-input">
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  id="currentPassword"
                  className="form-input"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  placeholder="Enter current password"
                  required
                />
                <button 
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="newPassword" className="form-label">New Password</label>
              <div className="password-input">
                <input
                  type={showNewPassword ? "text" : "password"}
                  id="newPassword"
                  className="form-input"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password"
                  required
                />
                <button 
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <div className="input-hint">
                Must be at least 6 characters
              </div>
            </div>
            
            <div className="form-group">
              <label htmlFor="confirmPassword" className="form-label">Confirm New Password</label>
              <div className="password-input">
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  id="confirmPassword"
                  className="form-input"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  required
                />
                <button 
                  type="button"
                  className="password-toggle"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            
            <button 
              type="submit" 
              className="update-password-btn"
              disabled={loading || !currentPassword || !newPassword || !confirmPassword}
            >
              {loading ? 'Updating...' : <><Lock size={18} /> Update Password</>}
            </button>
          </form>
        </div>
        
        {/* Preferences Card */}
        <div className="settings-card preferences-card">
          <div className="card-header">
            <h2>Preferences</h2>
            <Monitor size={20} />
          </div>
          
          <div className="preference-item">
            <div className="preference-info">
              <h3>Theme</h3>
              <p>Customize the appearance of your learning environment</p>
            </div>
            <div className="theme-toggle">
              <button className="theme-option light">
                <Sun size={18} />
                Light
              </button>
              <button className="theme-option dark">
                <Moon size={18} />
                Dark
              </button>
            </div>
          </div>
          
          <div className="preference-item">
            <div className="preference-info">
              <h3>Notifications</h3>
              <p>Manage how you receive updates about your courses</p>
            </div>
            <div className="notification-toggle">
              <label className="switch">
                <input type="checkbox" defaultChecked />
                <span className="slider"></span>
              </label>
            </div>
          </div>
        </div>
        
        {/* Danger Zone Card */}
        <div className="settings-card danger-card">
          <div className="card-header">
            <h2>Danger Zone</h2>
            <AlertTriangle size={20} />
          </div>
          
          <div className="danger-item">
            <div className="danger-info">
              <h3>Delete Account</h3>
              <p>Permanently delete your account and all associated data. This action cannot be undone.</p>
            </div>
            <button 
              className="delete-account-btn"
              onClick={() => setShowDeleteModal(true)}
              disabled={loading}
            >
              <Trash2 size={18} />
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {/* Delete Account Modal */}
      <DeleteAccountModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteAccount}
        loading={loading}
      />
    </div>
  );
};

export default MemberSettings;
